import { Toaster } from "@/components/ui/toaster"
import { Toaster as Sonner } from "@/components/ui/sonner"
import { TooltipProvider } from "@/components/ui/tooltip"
import { QueryClientProvider } from "@tanstack/react-query"
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom"
import { Layout } from "@/components/Layout"
import AdminCreateRouteGuard from "@/components/AdminCreateRouteGuard"
import CouncilDeletionGuard from "@/components/CouncilDeletionGuard"
import { useAuth } from "@/hooks/useAuth"
import { useAuthValidation } from "@/hooks/useAuthValidation"
import Index from "./pages/Index"
import DevicesPage from "./pages/DevicesPage"
import CustomersPage from "./pages/CustomersPage"
import ManageAdminPage from "./pages/ManageAdminPage"
import CreateAdminPage from "./pages/CreateAdminPage"
import AdminDetailsPage from "./pages/AdminDetailsPage"
import ManageWaterbureauPage from "./pages/ManageWaterbureauPage"
import RegisterWaterbureauPage from "./pages/RegisterWaterbureauPage"
import WaterbureauSettingsPage from "./pages/WaterbureauSettingsPage"
import MeterInfoPage from "./pages/MeterInfoPage"
import CouncilDashboardPage from "./pages/CouncilDashboardPage"
import DeviceDetailPage from "./pages/DeviceDetailPage"
import CustomerDetailPage from "./pages/CustomerDetailPage"
import LoginPage from "./pages/LoginPage"
import TwoFactorPage from "./pages/TwoFactorPage"
import NotFound from "./pages/NotFound"
import AddCustomerPage from "./pages/AddCustomerPage"
import WaterEfficiencyPage from "./pages/WaterEfficiencyPage"
import CouncilAppVersionPage from "./pages/CouncilAppVersionPage"
import InvitationResponsePage from "./pages/admin/invitations/InvitationResponsePage"
import { queryClient } from "./lib/queryClient"

// Component to handle authentication redirects
const AuthGuard = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth()
  const location = useLocation()

  // Allow access to invitation response page without authentication
  const isPublicRoute =
    location.pathname === "/login" ||
    location.pathname === "/two-factor" ||
    location.pathname.startsWith("/admin/invitations/respond/")

  if (!isAuthenticated && !isPublicRoute) {
    return <Navigate to="/login" replace />
  }

  if (isAuthenticated && location.pathname === "/login") {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}

const AuthValidator = ({ children }: { children: React.ReactNode }) => {
  useAuthValidation()
  return <>{children}</>
}

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthValidator>
            <AuthGuard>
              <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/two-factor" element={<TwoFactorPage />} />
                <Route
                  path="/"
                  element={
                    <Layout>
                      <Index />
                    </Layout>
                  }
                />
                <Route
                  path="/devices"
                  element={
                    <Layout>
                      <DevicesPage />
                    </Layout>
                  }
                />
                <Route
                  path="/customers"
                  element={
                    <Layout>
                      <CustomersPage />
                    </Layout>
                  }
                />
                <Route
                  path="/devices/:deviceId"
                  element={
                    <Layout>
                      <DeviceDetailPage />
                    </Layout>
                  }
                />
                <Route
                  path="/manage-admin"
                  element={
                    <Layout>
                      <ManageAdminPage />
                    </Layout>
                  }
                />
                <Route
                  path="/manage-admin/create"
                  element={
                    <Layout>
                      <AdminCreateRouteGuard>
                        <CreateAdminPage />
                      </AdminCreateRouteGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/manage-admin/council-admins"
                  element={
                    <Layout>
                      <ManageAdminPage mode="council-admins" />
                    </Layout>
                  }
                />
                <Route
                  path="/manage-admin/council-admins/:adminId"
                  element={
                    <Layout>
                      <AdminDetailsPage />
                    </Layout>
                  }
                />
                <Route
                  path="/manage-admin/:adminId"
                  element={
                    <Layout>
                      <AdminDetailsPage />
                    </Layout>
                  }
                />
                <Route
                  path="/manage-waterbureau"
                  element={
                    <Layout>
                      <ManageWaterbureauPage />
                    </Layout>
                  }
                />
                <Route
                  path="/manage-waterbureau/create"
                  element={
                    <Layout>
                      <RegisterWaterbureauPage />
                    </Layout>
                  }
                />
                <Route
                  path="/manage-waterbureau/:councilId/settings"
                  element={
                    <Layout>
                      <WaterbureauSettingsPage />
                    </Layout>
                  }
                />
                <Route
                  path="/meter-info"
                  element={
                    <Layout>
                      <MeterInfoPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId"
                  element={
                    <Layout>
                      <CouncilDeletionGuard>
                        <CouncilDashboardPage />
                      </CouncilDeletionGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/devices"
                  element={
                    <Layout>
                      <DevicesPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/customers"
                  element={
                    <Layout>
                      <CustomersPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/manage-admin"
                  element={
                    <Layout>
                      <ManageAdminPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/manage-admin/create"
                  element={
                    <Layout>
                      <AdminCreateRouteGuard>
                        <CreateAdminPage />
                      </AdminCreateRouteGuard>
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/manage-admin/:adminId"
                  element={
                    <Layout>
                      <AdminDetailsPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/devices/:deviceId"
                  element={
                    <Layout>
                      <DeviceDetailPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/add-customer"
                  element={
                    <Layout>
                      <AddCustomerPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/water-efficiency"
                  element={
                    <Layout>
                      <WaterEfficiencyPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/app-version"
                  element={
                    <Layout>
                      <CouncilAppVersionPage />
                    </Layout>
                  }
                />
                <Route
                  path="/council/:councilId/customers/:customerId"
                  element={
                    <Layout>
                      <CustomerDetailPage />
                    </Layout>
                  }
                />
                <Route
                  path="/admin/invitations/respond/:token"
                  element={<InvitationResponsePage />}
                />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </AuthGuard>
          </AuthValidator>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  )
}

export default App
